import { all, call, cancelled, delay, fork, put, take } from 'redux-saga/effects';
import { buffers, channel, eventChannel } from 'redux-saga';
import axios from 'axios';
import { Api } from '$api';
import { detectVideoContentType } from '$utils/multipartUpload';
import {
  apiOptions,
  classifyUploadError,
  isRetryableKind,
  probeFileReadable,
  retryDelay,
  STALL_TIMEOUT,
  withKind,
  withStallDetector,
} from '$utils/uploadRetry';
import {
  getUploadSession,
  removeUploadSession,
  saveUploadSession,
  touchUploadSession,
} from '$utils/uploadSessions';
import { AttachUploadStatus } from '$store/models';

const MIN_PART_SIZE = 10 * 1024 * 1024;
const MAX_PARTS = 1000;
const BATCH_SIZE = 3;
const SIGN_AHEAD = BATCH_SIZE * 3;
const NETWORK_WAIT_TIMEOUT = 15000;
const PROGRESS_THROTTLE = 250;
const MAX_REFRESHES = 2;

// 10 MB parts all the way up to the backend's 1000-part limit (10 GB),
// so a dropped connection loses at most BATCH_SIZE parts in flight.
export function calcPartSize(fileSize: number) {
  const partSize = Math.max(MIN_PART_SIZE, Math.ceil(fileSize / MAX_PARTS));
  return { partSize, parts: Math.max(1, Math.ceil(fileSize / partSize)) };
}

// How the generic upload machinery talks to the UI. Both callbacks are
// optional: callers without a modal (stories) report nothing.
export interface UploadReporter {
  progress?: (payload: { loaded: number; total: number }) => any;
  status?: (payload: { status: AttachUploadStatus; attempt?: number }) => any;
  uploadId?: (uploadId: string) => any;
  idleStatus?: AttachUploadStatus;
}

export interface UploadSession {
  uploadId: string;
  key: string;
  bucket: string;
  publicUrl: string;
  partSize: number;
  partsCount: number;
  fileSize: number;
  completedParts: Map<number, string>;
  fingerprint?: string;
  attempts: number;
  resumedParts: number;
}

// Resumable state lives here, not in redux: etags and URLs are of no use to the UI
// and would re-render it on every progress tick.
const sessions = new Map<string, UploadSession>();

export const getUploadSessionById = (uploadId: string) => sessions.get(uploadId);

const needsNetworkWait = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

function networkChannel() {
  return eventChannel((emit) => {
    const notify = () => emit(true);
    window.addEventListener('online', notify);
    window.addEventListener('pageshow', notify);
    document.addEventListener('visibilitychange', notify);
    // iOS does not always fire `online`; a periodic probe attempt is the fallback.
    const timer = setInterval(notify, NETWORK_WAIT_TIMEOUT);
    return () => {
      window.removeEventListener('online', notify);
      window.removeEventListener('pageshow', notify);
      document.removeEventListener('visibilitychange', notify);
      clearInterval(timer);
    };
  });
}

function* report(
  reporter: UploadReporter | undefined,
  status: AttachUploadStatus,
  attempt?: number,
) {
  if (reporter?.status) {
    yield put(reporter.status({ status, attempt }));
  }
}

export function* waitForNetwork(reporter?: UploadReporter) {
  if (!needsNetworkWait()) {
    return;
  }
  yield call(report, reporter, 'waiting_network');
  const chan = networkChannel();
  try {
    while (needsNetworkWait()) {
      yield take(chan);
    }
  } finally {
    chan.close();
  }
}

export interface RetryOptions {
  reporter?: UploadReporter;
  // Called on 'expired' (presigned URL is stale): refresh it and try again
  // without counting an attempt. Bounded by MAX_REFRESHES.
  refresh?: () => any;
  onAttempt?: (attempt: number) => void;
}

// Runs `task` until it succeeds, waiting for the network and backing off on
// retryable errors. Non-retryable errors are rethrown with `kind` attached.
export function* runWithRetry(task: () => any, options: RetryOptions = {}) {
  const { reporter, refresh, onAttempt } = options;
  let attempt = 0;
  let refreshes = 0;
  while (true) {
    try {
      const result = yield call(task);
      if (attempt > 0) {
        yield call(report, reporter, reporter?.idleStatus || 'uploading');
      }
      return result;
    } catch (e) {
      const kind = classifyUploadError(e);
      if (kind === 'expired' && refresh && refreshes < MAX_REFRESHES) {
        refreshes++;
        yield call(refresh);
        continue;
      }
      const wait = isRetryableKind(kind) ? retryDelay(++attempt) : null;
      if (wait === null) {
        throw withKind(e, kind);
      }
      onAttempt?.(attempt);
      yield call(waitForNetwork, reporter);
      yield call(report, reporter, 'retrying', attempt);
      if (wait > 0) {
        yield delay(wait);
      }
    }
  }
}

export function* apiWithRetry(url: string, body: any, reporter?: UploadReporter) {
  return yield call(runWithRetry, () => Api.post(url, body, apiOptions), { reporter });
}

// Forwards progress to redux at most every PROGRESS_THROTTLE ms; the sliding
// buffer keeps only the latest value in between.
export function* watchProgress(chan, progressAction: (payload: any) => any) {
  while (true) {
    yield put(progressAction(yield take(chan)));
    yield delay(PROGRESS_THROTTLE);
  }
}

function putChunk(
  url: string,
  chunk: Blob,
  onProgress: (event: ProgressEvent) => void,
  headers?: Record<string, string>,
) {
  const source = axios.CancelToken.source();
  const promise = withStallDetector<string>(
    (progress) =>
      axios
        .put(url, chunk, {
          headers,
          cancelToken: source.token,
          onUploadProgress: progress,
        })
        .then((res: any) => res.headers?.etag || ''),
    (reason) => source.cancel(reason),
    onProgress,
    STALL_TIMEOUT,
  );
  return { promise, cancel: (reason: string) => source.cancel(reason) };
}

// One PUT attempt with the readability probe in front of it. Tracks the
// in-flight request so saga cancellation can abort it, even if the cancel
// lands while the probe is still pending.
export function createPutTask(onProgress: (event: ProgressEvent) => void) {
  const state = { cancelled: false, current: null as ReturnType<typeof putChunk> | null };
  return {
    run: (file: File, url: string, chunk: Blob, headers?: Record<string, string>) =>
      probeFileReadable(file).then(() => {
        if (state.cancelled) {
          throw new axios.Cancel('cancel');
        }
        state.current = putChunk(url, chunk, onProgress, headers);
        return state.current.promise;
      }),
    cancel: () => {
      state.cancelled = true;
      state.current?.cancel('cancel');
    },
  };
}

function* signParts(
  uploadId: string,
  partNumbers: number[],
  asPeerId: number,
  reporter?: UploadReporter,
) {
  const data: { parts: { partNumber: number; url: string }[] } = yield call(
    apiWithRetry,
    '/video/upload_multipart/sign',
    { uploadId, partNumbers, asPeerId },
    reporter,
  );
  const urls: Record<number, string> = {};
  for (const part of data.parts) {
    urls[part.partNumber] = part.url;
  }
  return urls;
}

function* uploadPart(
  session: UploadSession,
  file: File,
  partNumber: number,
  url: string,
  onProgress: (event: ProgressEvent) => void,
  asPeerId: number,
  reporter?: UploadReporter,
) {
  const start = (partNumber - 1) * session.partSize;
  const end = Math.min(start + session.partSize, file.size);
  let currentUrl = url;
  const put = createPutTask(onProgress);

  try {
    const etag: string = yield call(
      runWithRetry,
      () => put.run(file, currentUrl, file.slice(start, end)),
      {
        reporter,
        refresh: function* () {
          const urls = yield call(
            signParts,
            session.uploadId,
            [partNumber],
            asPeerId,
            reporter,
          );
          currentUrl = urls[partNumber];
        },
        onAttempt: () => {
          session.attempts++;
        },
      },
    );
    session.completedParts.set(partNumber, etag);
  } finally {
    if (yield cancelled()) {
      put.cancel();
    }
  }
}

function* restoreSession(
  file: File,
  fingerprint: string,
  asPeerId: number,
  reporter?: UploadReporter,
) {
  const stored = getUploadSession(fingerprint);
  if (!stored || stored.fileSize !== file.size) {
    return null;
  }
  try {
    const data: {
      parts: { partNumber: number; etag: string; size: number }[];
      partSize: number;
      fileSize: number;
    } = yield call(
      apiWithRetry,
      '/video/upload_multipart/parts',
      { uploadId: stored.uploadId, asPeerId },
      reporter,
    );
    if (
      data.fileSize !== file.size ||
      (data.partSize && data.partSize !== stored.partSize)
    ) {
      removeUploadSession(fingerprint);
      return null;
    }
    const { partSize, partsCount } = stored;
    const session: UploadSession = {
      ...stored,
      fileSize: file.size,
      completedParts: new Map(),
      fingerprint,
      attempts: 0,
      resumedParts: 0,
    };
    for (const part of data.parts) {
      const expected =
        part.partNumber === partsCount
          ? file.size - (partsCount - 1) * partSize
          : partSize;
      if (part.size === expected) {
        session.completedParts.set(part.partNumber, part.etag);
      }
    }
    session.resumedParts = session.completedParts.size;
    return session;
  } catch (e) {
    if (e.kind === 'not_found' || e.kind === 'fatal') {
      removeUploadSession(fingerprint);
      return null;
    }
    throw e;
  }
}

function* createSession(
  file: File,
  asPeerId: number,
  fingerprint?: string,
  reporter?: UploadReporter,
) {
  const { partSize, parts: partsCount } = calcPartSize(file.size);
  const contentType: string = yield call(detectVideoContentType, file);
  const initData: any = yield call(
    apiWithRetry,
    '/video/upload_multipart',
    {
      contentType,
      parts: partsCount,
      fileSize: file.size,
      partSize,
      lazy: true,
      asPeerId,
    },
    reporter,
  );
  const session: UploadSession = {
    uploadId: initData.uploadId,
    key: initData.key,
    bucket: initData.bucket,
    publicUrl: initData.publicUrl,
    partSize,
    partsCount,
    fileSize: file.size,
    completedParts: new Map(),
    fingerprint,
    attempts: 0,
    resumedParts: 0,
  };
  if (fingerprint) {
    const { uploadId, key, bucket, publicUrl } = session;
    saveUploadSession(fingerprint, {
      uploadId,
      key,
      bucket,
      publicUrl,
      partSize,
      partsCount,
      fileSize: file.size,
    });
  }
  return session;
}

function forgetSession(session: UploadSession) {
  sessions.delete(session.uploadId);
  if (session.fingerprint) {
    removeUploadSession(session.fingerprint);
  }
}

// Only the first call for a session reaches the backend; cancel is dispatched
// several times during modal teardown.
export function* abortSession(uploadId: string, asPeerId = 0) {
  const session = sessions.get(uploadId);
  if (!session) {
    return;
  }
  forgetSession(session);
  try {
    yield call(
      Api.post,
      '/video/upload_multipart/abort',
      { uploadId, asPeerId },
      apiOptions,
    );
  } catch {}
}

export interface MultipartUploadResult {
  uploadData: { publicUrl: string; key: string; bucket: string };
  stats: { uploadId: string; attempts: number; resumedParts: number; partsCount: number };
}

export function* multipartUploadSaga(
  file: File,
  reporter?: UploadReporter,
  asPeerId = 0,
  fingerprint?: string,
  resumeUploadId?: string,
) {
  const progressChan = channel(buffers.sliding(1));
  if (reporter?.progress) {
    yield fork(watchProgress, progressChan, reporter.progress);
  }

  let session: UploadSession | undefined = resumeUploadId
    ? sessions.get(resumeUploadId)
    : undefined;

  try {
    const current: UploadSession =
      session ||
      (fingerprint &&
        (yield call(restoreSession, file, fingerprint, asPeerId, reporter))) ||
      (yield call(createSession, file, asPeerId, fingerprint, reporter));
    session = current;
    sessions.set(current.uploadId, current);
    if (reporter?.uploadId) {
      yield put(reporter.uploadId(current.uploadId));
    }
    yield call(report, reporter, 'uploading');

    const partBytes = (partNumber: number) =>
      Math.min(partNumber * current.partSize, file.size) -
      (partNumber - 1) * current.partSize;
    let completedBytes = 0;
    current.completedParts.forEach((_, partNumber) => {
      completedBytes += partBytes(partNumber);
    });
    const inFlight: Record<number, number> = {};
    const reportProgress = () => {
      let loaded = completedBytes;
      for (const partNumber in inFlight) {
        loaded += inFlight[partNumber];
      }
      progressChan.put({ loaded, total: file.size });
    };
    reportProgress();

    const pending: number[] = [];
    for (let n = 1; n <= current.partsCount; n++) {
      if (!current.completedParts.has(n)) pending.push(n);
    }

    // Sign a few batches ahead so the channel never idles on a sign round-trip.
    for (let i = 0; i < pending.length; i += SIGN_AHEAD) {
      const signed = pending.slice(i, i + SIGN_AHEAD);
      const urls: Record<number, string> = yield call(
        signParts,
        current.uploadId,
        signed,
        asPeerId,
        reporter,
      );
      if (current.fingerprint) {
        touchUploadSession(current.fingerprint);
      }

      for (let j = 0; j < signed.length; j += BATCH_SIZE) {
        const batch = signed.slice(j, j + BATCH_SIZE);
        yield all(
          batch.map((partNumber) =>
            call(
              uploadPart,
              current,
              file,
              partNumber,
              urls[partNumber],
              (event) => {
                inFlight[partNumber] = event.loaded;
                reportProgress();
              },
              asPeerId,
              reporter,
            ),
          ),
        );
        batch.forEach((partNumber) => {
          delete inFlight[partNumber];
          completedBytes += partBytes(partNumber);
        });
        reportProgress();
      }
    }

    const parts = Array.from(current.completedParts.entries())
      .map(([partNumber, etag]) => ({ partNumber, etag }))
      .sort((a, b) => a.partNumber - b.partNumber);

    yield call(
      apiWithRetry,
      '/video/upload_multipart/complete',
      {
        uploadId: current.uploadId,
        key: current.key,
        bucket: current.bucket,
        publicUrl: current.publicUrl,
        parts,
        asPeerId,
      },
      reporter,
    );

    forgetSession(current);

    const result: MultipartUploadResult = {
      uploadData: {
        publicUrl: current.publicUrl,
        key: current.key,
        bucket: current.bucket,
      },
      stats: {
        uploadId: current.uploadId,
        attempts: current.attempts,
        resumedParts: current.resumedParts,
        partsCount: current.partsCount,
      },
    };
    return result;
  } catch (e) {
    // Session vanished or the server rejected it for good: nothing to resume,
    // the next attempt starts from scratch.
    if (session && (e.kind === 'not_found' || e.kind === 'fatal')) {
      forgetSession(session);
    }
    throw e;
  } finally {
    progressChan.close();
  }
}
